/* eslint-disable no-param-reassign */
import { get } from "lodash-es";
import { compPathToSourcePath } from './path';
import { memoWithWeakMap } from '@tencent/data-mapper';
/**
 * @param path 例如 "/default/0/default/1" 这种
 */
export function getLayoutAtPath(layout, path) {
  if (!layout) return null;
  let ptr = layout;
  if (!path || path[0] !== '/' || path.length < 2) return ptr;
  const indices = path.substr(1).split('/');
  while (ptr && indices.length >= 2) {
    var _getLayoutSlot;
    const slotId = indices.shift();
    const index = +indices.shift();
    ptr = (_getLayoutSlot = getLayoutSlot(ptr, slotId, false)) === null || _getLayoutSlot === void 0 ? void 0 : _getLayoutSlot[index];
  }
  return ptr;
}
export function getLayoutFromPagelet(pagelet, compPath) {
  if (!pagelet) return;
  const sourcePath = compPathToSourcePath(compPath);
  return get(pagelet, sourcePath);
}
/**
 * 用于获取一个 slot 数组。如果不存在，会先创建一个。
 *
 * @param createIfNotExists 默认的如果不存在，会先创建一个。可通过这个选项关闭此功能
 */
export function getLayoutSlot(layout, slotId, createIfNotExists = true) {
  var _layout$slots;
  if (!layout) return null;
  let slotArray = slotId === 'default' ? layout.children : (_layout$slots = layout.slots) === null || _layout$slots === void 0 ? void 0 : _layout$slots[slotId];
  if (!slotArray && createIfNotExists) {
    if (slotId === 'default') {
      layout.children = [];
      slotArray = layout.children;
    } else {
      if (!layout.slots) layout.slots = {};
      layout.slots[slotId] = []; // layout.slots[slotId] = [];
      slotArray = layout.slots[slotId];
    }
  }
  return slotArray;
}
/**
 * 遍历整个布局树（注意：scopedSlot 不会遍历）
 *
 * @param layout
 * @param handler 返回 abort 可终止全部；返回 skip 可跳过当前的 children
 * @param opts
 */
export function walkLayout(layout, handler, opts = {}) {
  if (!layout) return;
  const walkPending = [{
    layout,
    slotId: '',
    compPath: '',
    sourcePath: ''
  }];
  while (walkPending.length) {
    const parent = walkPending.shift();
    if (!parent || !parent.layout || !parent.layout.type) continue; // skip invalid layout
    const handlerAns = handler(parent);
    if (handlerAns === 'abort') break;
    if (handlerAns === 'skip') continue;
    const namedSlots = parent.layout.slots;
    if (namedSlots) {
      Object.keys(namedSlots).forEach(slotId => {
        const slotChildren = namedSlots[slotId];
        if (!slotChildren || !Array.isArray(slotChildren)) return;
        walkPending.push(...slotChildren.map((subLayout, index) => ({
          layout: subLayout,
          slotId,
          parent,
          compPath: `${parent.compPath}/${slotId}/${index}`,
          sourcePath: `${parent.sourcePath}/slots/${slotId}/${index}`
        })));
      });
    }
    const defaultSlot = parent.layout.children;
    if (defaultSlot && Array.isArray(defaultSlot)) {
      const slotId = 'default';
      defaultSlot.forEach((subLayout, index) => {
        walkPending.push({
          layout: subLayout,
          slotId,
          parent,
          compPath: `${parent.compPath}/${slotId}/${index}`,
          sourcePath: `${parent.sourcePath}/children/${index}`
        });
      });
    }
    const scopedSlots = (opts === null || opts === void 0 ? void 0 : opts.includeScopedSlots) && parent.layout.scopedSlots;
    if (scopedSlots) {
      Object.keys(scopedSlots).forEach(slotId => {
        var _opts$pageletHandler;
        // PROBLEM: parent 是空的
        const {
          layout
        } = scopedSlots[slotId];
        // 迭代作用域插槽里的pagelet
        (_opts$pageletHandler = opts.pageletHandler) === null || _opts$pageletHandler === void 0 || _opts$pageletHandler.call(opts, {
          pagelet: scopedSlots[slotId],
          sourcePath: `${parent.sourcePath}/scopedSlots/${slotId}`
        });
        walkPending.push({
          layout,
          slotId: '',
          compPath: '',
          sourcePath: `${parent.sourcePath}/scopedSlots/${slotId}/layout`
        });
      });
    }
  }
}
/**
 * 根据组件的 componentId 生成 LUT
 */
export function getLayoutIdLUT(layout, opts) {
  var _opts$compPathPrefix, _opts$sourcePathPrefi;
  const compPathPrefix = (_opts$compPathPrefix = opts === null || opts === void 0 ? void 0 : opts.compPathPrefix) !== null && _opts$compPathPrefix !== void 0 ? _opts$compPathPrefix : '';
  const sourcePathPrefix = (_opts$sourcePathPrefi = opts === null || opts === void 0 ? void 0 : opts.sourcePathPrefix) !== null && _opts$sourcePathPrefi !== void 0 ? _opts$sourcePathPrefi : '';
  const ans = {};
  walkLayout(layout, step => {
    const id = step.layout.componentId;
    if (!id) return;
    ans[id] = {
      layout: step.layout,
      compPath: compPathPrefix + step.compPath,
      sourcePath: sourcePathPrefix + step.sourcePath
    };
  });
  return ans;
}
/**
 * 根据组件的 componentId 生成 LUT，和 getLayoutIdLUT 不同的是，输入的是 pagelet，也就是会统计 triggers 等东西
 */
export function getLayoutIdLUTFromPagelet(pagelet, opts) {
  var _opts$compPathPrefix2, _opts$sourcePathPrefi2, _pagelet$triggers;
  const ans = {};
  const compPathPrefix = (_opts$compPathPrefix2 = opts === null || opts === void 0 ? void 0 : opts.compPathPrefix) !== null && _opts$compPathPrefix2 !== void 0 ? _opts$compPathPrefix2 : '';
  const sourcePathPrefix = (_opts$sourcePathPrefi2 = opts === null || opts === void 0 ? void 0 : opts.sourcePathPrefix) !== null && _opts$sourcePathPrefi2 !== void 0 ? _opts$sourcePathPrefi2 : '';
  Object.assign(ans, getLayoutIdLUT(pagelet.layout, {
    compPathPrefix: `${compPathPrefix}`,
    sourcePathPrefix: `${sourcePathPrefix}/layout`
  }));
  (_pagelet$triggers = pagelet.triggers) === null || _pagelet$triggers === void 0 || _pagelet$triggers.forEach((triggerLayout, triggerIndex) => {
    Object.assign(ans, getLayoutIdLUT(triggerLayout, {
      compPathPrefix: `${compPathPrefix}/@triggers/${triggerIndex}`,
      sourcePathPrefix: `${sourcePathPrefix}/triggers/${triggerIndex}`
    }));
  });
  return ans;
}
/**
 * 和 getLayoutIdLUTFromPagelet 一样，但是会有 immutable 加持，返回缓存的结果
 *
 * 确保传入的参数是 immutable 的啊！
 */
// eslint-disable-next-line max-len
export const cachedGetLayoutIdLUTFromPagelet = memoWithWeakMap(pagelet => getLayoutIdLUTFromPagelet(pagelet));
/**
 * 和 getLayoutIdLUTFromPagelet 一样，但是会有 immutable 加持，返回缓存的结果
 *
 * 确保传入的参数是 immutable 的啊！
 */
export const cachedGetLayoutIdLUT = memoWithWeakMap(layout => getLayoutIdLUT(layout));
/** 看一下组件内部是否还有别的组件 */
export function isLayoutHasChild(layout) {
  const hasChildren = Array.isArray(layout.children) && layout.children.length > 0;
  return hasChildren || layout.slots || layout.scopedSlots;
}
//# sourceMappingURL=getters.js.map
